import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery, gql } from "@apollo/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormInput } from "@/pages/participantDocuments/lib/formRender";
import { getFormBehavior } from "@/pages/participantDocuments/behaviors/registry";
import { toast } from "@/components/ui/toast";
import { FileText } from "lucide-react";
import { SectionHeader } from "./SectionHeader";

const LIST_FORM_TEMPLATES = gql`
  query listFormTemplates {
    listFormTemplates {
      id
      name
      body
    }
  }
`;

const CREATE_PARTICIPANT_DOCUMENT = gql`
  mutation CreateParticipantDocument($updates: GenericJSONDocumentInput!) {
    createParticipantDocument(updates: $updates) {
      id
      type
    }
  }
`;

const GET_PARTICIPANT_BY_ODRNUMBER = gql`
  query GetParticipantByODRNumber($odrnumber: Int!) {
    getParticipantByODRNumber(odrnumber: $odrnumber) {
      id
      cin
      name {
        firstName
        lastName
      }
    }
  }
`;

const GET_PARTICIPANT_CAREPLAN_GOALS = gql`
  query GetParticipantCarePlanGoals($participantID: String!) {
    getParticipantCarePlanGoals(participantID: $participantID) {
      start_date
      due_date
      problem
      priority
      identified_strengths
      identified_barriers
      smart_goal
      intervention_steps
      goal_status
      outcome_narrative
    }
  }
`;

interface DocumentEntryFormProps {
  odrNumber: string;
  user: any;
  showHeader?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
  refetch: () => void;
}

export const DocumentEntryForm: React.FC<DocumentEntryFormProps> = ({
  odrNumber,
  user,
  showHeader = true,
  onSuccess,
  onCancel,
  refetch,
}) => {
  const [selectedType, setSelectedType] = useState<string>("");
  const [newDocumentState, setNewDocumentState] = useState<any>({});
  const [isAltView, setIsAltView] = useState(false);

  const { data: participantData } = useQuery(GET_PARTICIPANT_BY_ODRNUMBER, {
    variables: { odrnumber: parseInt(odrNumber, 10) },
    skip: !odrNumber,
  });

  const {
    data: templateData,
    loading: templatesLoading,
    error: templatesError,
  } = useQuery(LIST_FORM_TEMPLATES);

  const [loadGoals, { data: goalsData }] = useLazyQuery(
    GET_PARTICIPANT_CAREPLAN_GOALS
  );

  const [createDocument, { loading: creating }] = useMutation(
    CREATE_PARTICIPANT_DOCUMENT
  );

  const templateTypesFull = useMemo(() => {
    if (!templateData?.listFormTemplates) return [];
    const cloned = templateData.listFormTemplates.map((template: any) => ({
      ...template,
    }));

    cloned.forEach((template: any) => {
      if (template.name !== "Care Plan") return;
      const altTemplate = cloned.find((t: any) => t.name === "_Care Plan");
      if (!altTemplate) return;
      template.altTemplate = altTemplate.body;
    });

    return cloned;
  }, [templateData]);

  const templatesByType = useMemo(() => {
    if (!templateTypesFull.length) return {};
    return templateTypesFull.reduce((acc: any, template: any) => {
      acc[template.name] = template;
      return acc;
    }, {});
  }, [templateTypesFull]);

  const templateOptions = useMemo(() => {
    if (!templateTypesFull.length) return [];
    return templateTypesFull.filter((template: any) => !template.name.startsWith("_"));
  }, [templateTypesFull]);

  const getFormTemplate = (formType: string) => {
    const behavior = getFormBehavior(formType);
    const templateDataForType = templatesByType[formType];
    if (behavior.getTemplate) {
      return behavior.getTemplate(templateDataForType, isAltView);
    }
    return templateDataForType?.body || "{}";
  };

  const applyInitialData = (formType: string) => {
    const behavior = getFormBehavior(formType);
    let initialData = {};
    if (behavior.getInitialData) {
      initialData = behavior.getInitialData({
        participant: participantData?.getParticipantByODRNumber,
        user,
      });
    }
    setNewDocumentState(initialData || {});
  };

  useEffect(() => {
    if (selectedType !== "Care Plan") return;
    if (!goalsData?.getParticipantCarePlanGoals?.length) return;

    const goals = goalsData.getParticipantCarePlanGoals.map((goal: any) => ({
      start_date: goal.start_date || "",
      due_date: goal.due_date || "",
      problem: goal.problem || "",
      priority: goal.priority || "",
      identified_strengths: goal.identified_strengths || "",
      identified_barriers: goal.identified_barriers || "",
      smart_goal: goal.smart_goal || "",
      intervention_steps: goal.intervention_steps || "",
      goal_status: goal.goal_status || "in_progress",
      outcome_narrative: goal.outcome_narrative || "",
      import_to_next: false,
    }));

    setNewDocumentState((prev: any) => ({
      ...prev,
      goals,
    }));
    toast.success(`Imported ${goals.length} incomplete goals from previous care plans`);
  }, [goalsData, selectedType]);

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    setIsAltView(false);
    if (!value) {
      setNewDocumentState({});
      return;
    }
    applyInitialData(value);
    if (value !== "Care Plan") return;
    loadGoals({ variables: { participantID: odrNumber.toString() } });
  };

  const handleSubmit = async () => {
    if (!selectedType) {
      toast.error("Please select a document type");
      return;
    }

    const behavior = getFormBehavior(selectedType);
    let payload = newDocumentState || {};
    if (behavior.customLogic?.onBeforeCreate) {
      payload = behavior.customLogic.onBeforeCreate(payload);
    }

    try {
      await createDocument({
        variables: {
          updates: {
            contents: JSON.stringify(payload),
            published: 0,
            metadata: "{}",
            type: selectedType,
            odrparticipant: parseInt(odrNumber, 10),
            template: getFormTemplate(selectedType),
          },
        },
        refetchQueries: ["GetSummaryData"],
        awaitRefetchQueries: true,
      });
      toast.success("Document Created");
      refetch();
      onSuccess();
    } catch (error) {
      console.error("Error creating document", error);
      toast.error("Error creating document");
    }
  };

  if (templatesLoading) {
    return (
      <div className="py-6 text-sm text-gray-600">Loading document templates...</div>
    );
  }

  if (templatesError) {
    return (
      <div className="py-6 text-sm text-red-600">Error loading templates.</div>
    );
  }

  const currentTemplate = selectedType ? getFormTemplate(selectedType) : null;

  return (
    <div className="space-y-4">
      {showHeader && (
        <SectionHeader
          icon={FileText}
          title="Participant Document"
          subtitle="Create or upload a document"
        />
      )}
      <div className="grid grid-cols-[2fr_1fr] gap-3">
        <div className="space-y-2">
          <Label htmlFor="document-type">Document Type</Label>
          <Select value={selectedType} onValueChange={handleTypeChange}>
            <SelectTrigger id="document-type">
              <SelectValue placeholder="Select document type..." />
            </SelectTrigger>
            <SelectContent>
              {templateOptions.map((template: any) => (
                <SelectItem key={template.id} value={template.name}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedType === "Care Plan" && (
          <div className="space-y-2 pt-6">
            <Button
              variant="outline"
              onClick={() => setIsAltView((prev) => !prev)}
              id="document-alt-toggle"
            >
              {isAltView ? "Full Care Plan" : "Mini Care Plan View"}
            </Button>
          </div>
        )}
      </div>

      {currentTemplate && (
        <div className="border-t pt-4">
          <FormInput
            formTemplate={JSON.parse(currentTemplate)}
            formState={newDocumentState}
            setFormState={setNewDocumentState}
            onSubmit={handleSubmit}
            onClose={onCancel}
          />
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button size="sm" onClick={handleSubmit} disabled={creating} id="document-save">
          {creating ? "Saving..." : "Save Document"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} id="document-cancel">
          Cancel
        </Button>
      </div>
    </div>
  );
};
